import AddAddressModal from '~/components/modals/person/AddAddressModal/AddAddressModal.vue';
import AddAlternateNameModal from '~/components/modals/person/AddAlternateNameModal/AddAlternateNameModal.vue';
import AddChildConsent from '~/components/modals/person/AddContact/AddChildConsent.vue';
import AddChildForm from '~/components/modals/person/AddContact/AddChildForm.vue';
import AddContact from '~/components/modals/person/AddContact/AddContact.vue';
import AddMemberTypeSelect from '~/components/modals/person/AddContact/AddMemberTypeSelect.vue';
import AddEmailModal from '~/components/modals/person/AddEmailModal/AddEmailModal.vue';
import AddPhoneModal from '~/components/modals/person/AddPhoneModal/AddPhoneModal.vue';
import AssignLicenseModal from '~/components/modals/person/AssignLicenseModal/AssignLicenseModal.vue';
import GiveLicenseModal from '~/components/modals/person/GiveLicenseModal/GiveLicenseModal.vue';
import CallConnectedModal from '~/components/modals/person/CallConnectedModal/CallConnectedModal.vue';
import DeleteAccount from '~/components/modals/person/DeleteAccount/DeleteAccount.vue';
import EditDobModal from '~/components/modals/person/EditDobModal/EditDobModal.vue';
import EditNameModal from '~/components/modals/person/EditNameModal.vue';
import EmailVerification from '~/components/modals/person/EmailVerification/EmailVerification.vue';
import GiftCertModal from '~/components/modals/person/GiftCertModal/GiftCertModal.vue';
import GiftCertPurchased from '~/components/modals/person/GiftCertPurchasedModal/GiftCertPurchased.vue';
import ImageViewerModal from '~/components/modals/person/ImageViewerModal/ImageViewerModal.vue';
import OutgoingCallModal from '~/components/modals/person/OutgoingCallModal/OutgoingCallModal.vue';
import PhoneVerification from '~/components/modals/person/PhoneVerification/PhoneVerification.vue';
import RequestNewCompany from '~/components/modals/person/RequestNewCompany.vue';
import SubmitResearchRequest from '~/components/modals/person/SubmitResearchRequest.vue';
import StripePaymentModal from '~/components/modals/person/UpdatePaymentInfoModal/StripePaymentModal.vue';
import ManagePaymentMethodsModal from '~/components/modals/person/ManagePaymentMethodsModal/ManagePaymentMethodsModal.vue';
import ExposureSideBar from '~/components/side-bar/person/ExposureSideBar.vue';
import ExposureSources from '~/components/modals/person/ExposureSources/ExposureSources.vue';
import RemovalRequestModal from '~/components/modals/person/RemovalRequestModal/RemovalRequestModal.vue';
import CreatePasskeyModal from '~/components/modals/person/CreatePasskeyModal/CreatePasskeyModal.vue';
import type { Address, Aka, License } from './person/person';

import type { DialogProps } from 'primevue/dialog';
import type { DynamicDialogInstance, DynamicDialogOptions } from 'primevue/dynamicdialogoptions';
import DeletionRequestModal from '~/components/modals/person/DeletionRequestModal/DeletionRequestModal.vue';
import PurchaseLicensesModal from '~/components/modals/person/LicenseModals/PurchaseLicensesModal.vue';
import RefundLicenseModal from '~/components/modals/person/LicenseModals/RefundLicenseModal.vue';
import type { AssignLicenseModalData, PurchaseLicenseFor, PurchaseLicenseModalData } from '~/types/licenses/licenses';

export interface PbDialogProps extends DialogProps {
    onClose?: () => void;
}

export enum ModalNames {
    // Personal modals
    GIFTCERT,
    GIFTCERT_PURCHASED,
    MANAGE_PAYMENT_METHODS,
    EMAIL_VERIFICATION,
    PHONE_VERIFICATION,
    ADD_CONTACT,
    ADD_MEMBER_TYPE_SELECT,
    ADD_CHILD_CONSENT,
    ADD_CHILD_FORM,
    EXPOSURE_SIDEBAR,
    REQUEST_NEW_COMPANY,
    DELETION_REQUEST,
    OUTGOING_CALL,
    OUTGOING_CALL_CONNECTED,
    ADD_EMAIL,
    ADD_PHONE,
    ADD_ALTERNATE_NAME,
    EDIT_NAME,
    ADD_ADDRESS,
    EDIT_DOB,
    STRIPE_PAYMENT,
    IMAGE_VIEWER,
    ASSIGN_LICENSE,
    GIVE_LICENSE,
    SUBMIT_RESEARCH_REQUEST,
    REFUND_LICENSE,
    DELETE_ACCOUNT,
    EXPOSURE_SOURCES,
    REMOVAL_REQUEST,
    CREATE_PASSKEY,

    // License modals
    PURCHASE_LICENSES,

    // Vault edit modals
    EDIT_ADDRESS,
    EDIT_ALTERNATE_NAME,
}

// Type mapping for modal data requirements
export interface ModalDataMap {
    [ModalNames.PURCHASE_LICENSES]: PurchaseLicenseModalData;
    [ModalNames.REFUND_LICENSE]: License;
    [ModalNames.EMAIL_VERIFICATION]: { email: string; fromExposure?: boolean; exposureData?: any };
    [ModalNames.PHONE_VERIFICATION]: { phone_number: string; country_code: string; method: string; fromExposure?: boolean; exposureData?: any };
    [ModalNames.ADD_PHONE]: { phone_number?: string; country_code?: string; method?: string; fromExposure?: boolean; exposureData?: any };
    [ModalNames.ADD_EMAIL]: { email?: string; fromExposure?: boolean; exposureData?: any };
    [ModalNames.ADD_ADDRESS]: { address?: any; fromExposure?: boolean; exposureData?: any };
    [ModalNames.ADD_CONTACT]: { contact?: any; fromExposure?: boolean; exposureData?: any };
    [ModalNames.ADD_ALTERNATE_NAME]: { fullName?: string; fromExposure?: boolean; exposureData?: any };
    [ModalNames.EDIT_ADDRESS]: { editAddress: Address };
    [ModalNames.EDIT_ALTERNATE_NAME]: { editAka: Aka };
    [ModalNames.IMAGE_VIEWER]: { imageUrl: string; altText?: string };
    [ModalNames.ASSIGN_LICENSE]: AssignLicenseModalData;
    [ModalNames.GIVE_LICENSE]: { contact: PurchaseLicenseFor };
    [ModalNames.DELETION_REQUEST]: { company: { id: number; name: string; type: string; logo: string; url: string; slug: string } };
    [ModalNames.EXPOSURE_SOURCES]: { exposures: any[] };
    [ModalNames.MANAGE_PAYMENT_METHODS]: { openAdd?: boolean };
}

// Helper type to get data type for a specific modal
export type ModalData<T extends ModalNames> = T extends keyof ModalDataMap ? ModalDataMap[T] : never;

// Modals that carry a data payload
export type ModalWithData = keyof ModalDataMap;

// Modals whose payload has at least one required field — data must be passed when opening
export type ModalWithRequiredData = {
    [K in ModalWithData]: Partial<ModalDataMap[K]> extends ModalDataMap[K] ? never : K;
}[ModalWithData];

// Modals whose payload is entirely optional — data may be omitted when opening
export type ModalWithOptionalData = Exclude<ModalWithData, ModalWithRequiredData>;

// Modals that take no data payload
export type ModalWithoutData = Exclude<ModalNames, ModalWithData>;

// Modal component mapping for better autocomplete and navigation
export const ModalComponents = {
    [ModalNames.PHONE_VERIFICATION]: () => PhoneVerification,
    [ModalNames.EMAIL_VERIFICATION]: () => EmailVerification,
    [ModalNames.ADD_PHONE]: () => AddPhoneModal,
    [ModalNames.ADD_EMAIL]: () => AddEmailModal,
    [ModalNames.ADD_ADDRESS]: () => AddAddressModal,
    [ModalNames.ADD_ALTERNATE_NAME]: () => AddAlternateNameModal,
    [ModalNames.EDIT_ADDRESS]: () => AddAddressModal,
    [ModalNames.EDIT_ALTERNATE_NAME]: () => AddAlternateNameModal,
    [ModalNames.EDIT_NAME]: () => EditNameModal,
    [ModalNames.EDIT_DOB]: () => EditDobModal,
    [ModalNames.ADD_CONTACT]: () => AddContact,
    [ModalNames.ADD_MEMBER_TYPE_SELECT]: () => AddMemberTypeSelect,
    [ModalNames.ADD_CHILD_CONSENT]: () => AddChildConsent,
    [ModalNames.ADD_CHILD_FORM]: () => AddChildForm,
    [ModalNames.MANAGE_PAYMENT_METHODS]: () => ManagePaymentMethodsModal,
    [ModalNames.STRIPE_PAYMENT]: () => StripePaymentModal,
    [ModalNames.GIFTCERT]: () => GiftCertModal,
    [ModalNames.GIFTCERT_PURCHASED]: () => GiftCertPurchased,
    [ModalNames.REQUEST_NEW_COMPANY]: () => RequestNewCompany,
    [ModalNames.DELETION_REQUEST]: () => DeletionRequestModal,
    [ModalNames.OUTGOING_CALL]: () => OutgoingCallModal,
    [ModalNames.OUTGOING_CALL_CONNECTED]: () => CallConnectedModal,
    [ModalNames.IMAGE_VIEWER]: () => ImageViewerModal,
    [ModalNames.ASSIGN_LICENSE]: () => AssignLicenseModal,
    [ModalNames.GIVE_LICENSE]: () => GiveLicenseModal,
    [ModalNames.SUBMIT_RESEARCH_REQUEST]: () => SubmitResearchRequest,
    [ModalNames.PURCHASE_LICENSES]: () => PurchaseLicensesModal,
    [ModalNames.EXPOSURE_SIDEBAR]: () => ExposureSideBar,
    [ModalNames.REFUND_LICENSE]: () => RefundLicenseModal,
    [ModalNames.DELETE_ACCOUNT]: () => DeleteAccount,
    [ModalNames.EXPOSURE_SOURCES]: () => ExposureSources,
    [ModalNames.REMOVAL_REQUEST]: () => RemovalRequestModal,
    [ModalNames.CREATE_PASSKEY]: () => CreatePasskeyModal,
} as const;

export type TypedDialogOptions<T> = Omit<DynamicDialogOptions, 'data'> & { data: T };
export type TypedDialogInstance<T> = Omit<DynamicDialogInstance, 'data' | 'options'> & {
  data: T;
  options: TypedDialogOptions<T>;
};

// Helper type to get data type for a specific modal component
export type ModalDataForComponent<T extends keyof typeof ModalComponents> =
  T extends keyof ModalDataMap ? ModalDataMap[T] : never;
